import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { packagesAPI } from '../services/api';
import {
  prepareReceipt,
  humanSize,
  ReceiptError,
  type PreparedReceipt,
} from '../utils/receipt';
import './Packages.css';

/**
 * Checkout for a paid package — slice 3.
 *
 * A real URL rather than a panel inside /packages, because the bot will
 * deep-link straight here: Telebirr's H5 flow cannot run inside a Telegram
 * chat, so the bot's job is to hand the student this link.
 *
 * Three states, driven by the server's `mode`:
 *
 *   off    payment is not open. Say so, and say nothing has been charged.
 *   stub   a confirm button that settles instantly. Preview accounts only —
 *          the server refuses it for anyone else regardless of this UI.
 *   manual bank transfer with a person in the loop — what launch runs on.
 *          Bank details, then a receipt, then a wait. Every one of those three
 *          is a state the student can see, because the wait is the part this
 *          market handles worst.
 *   live   the real provider (not built yet; this page will hand off to it).
 *
 * The copy exists because of one specific market failure: students pay, hear
 * nothing, and are left saying "ከፍye" — I've paid — with no way to tell whether
 * anything happened. Every state here states plainly whether money moved.
 */

type Checkout = Awaited<ReturnType<typeof packagesAPI.checkout>>;

const Checkout: React.FC = () => {
  const { cardKey = '' } = useParams<{ cardKey: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<Checkout | null>(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Manual (bank transfer) flow.
  const [starting, setStarting] = useState(false);
  const [proof, setProof] = useState<PreparedReceipt | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [sending, setSending] = useState(false);
  // A student who has already sent a receipt may want to send another — they
  // photographed the wrong screen, or sent it before the transfer cleared.
  // The request stays `pending` either way, so the server takes the second one
  // and overwrites the first; this only decides whether we show the form.
  const [resending, setResending] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);
  /** Which number was just copied, so the button can say so. */
  const [copied, setCopied] = useState<string | null>(null);

  const copyNumber = useCallback(async (value?: string | null) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Older WebViews, and any insecure context, have no clipboard API. The
      // number is still on screen to read, so this is a lost convenience
      // rather than a lost payment.
      return;
    }
    setCopied(value);
    setTimeout(() => setCopied((c) => (c === value ? null : c)), 2000);
  }, []);

  /** Object URLs are leaked unless someone revokes them, and this page can
   *  hold several over a session as the student re-picks. */
  const clearProof = useCallback(() => {
    setProof((old) => {
      if (old) URL.revokeObjectURL(old.previewUrl);
      return null;
    });
    if (fileInput.current) fileInput.current.value = '';
  }, []);

  useEffect(() => clearProof, [clearProof]);

  const pickProof = useCallback(
    async (file: File | null | undefined) => {
      clearProof();
      setError(null);
      if (!file) return;
      setPreparing(true);
      try {
        setProof(await prepareReceipt(file));
      } catch (e: any) {
        setError(
          e instanceof ReceiptError
            ? e.message
            : "We couldn't read that file. Try a screenshot."
        );
      } finally {
        setPreparing(false);
      }
    },
    [clearProof]
  );

  const load = useCallback(async () => {
    try {
      setData(await packagesAPI.checkout(cardKey));
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not load this package.');
    } finally {
      setLoading(false);
    }
  }, [cardKey]);

  useEffect(() => {
    load();
  }, [load]);

  const pay = async () => {
    setPaying(true);
    setError(null);
    try {
      await packagesAPI.confirmCheckout(cardKey);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'That did not go through.');
    } finally {
      setPaying(false);
    }
  };

  /** Claim (or re-claim) the payment request, so an upload has somewhere to go. */
  const startBank = async () => {
    setStarting(true);
    setError(null);
    try {
      await packagesAPI.startBankPayment(cardKey);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail?.message || 'Could not start the payment.');
    } finally {
      setStarting(false);
    }
  };

  /** Upload the receipt and hand it to the admin queue, in that order.
   *
   *  The photo is the whole submission. There was a transaction-number field
   *  here and it was required, which meant a student who had photographed
   *  their receipt still had to transcribe a number off it — and if they did
   *  not, the button silently did nothing, because the guard returned before
   *  any request was made. The receipt is what an admin actually reads. */
  const sendProof = async (requestId: string) => {
    if (!proof) {
      setError('Add a photo of your receipt.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      await packagesAPI.uploadPaymentProof(requestId, proof.file);
      await packagesAPI.submitPayment(requestId, {});
      clearProof();
      setResending(false);
      await load();
    } catch (e: any) {
      // "That did not go through" was all this said for a 413 the app never
      // saw, and it sent the student round the same loop three times. Name
      // what happened, and say what to do about it.
      const status = e?.response?.status;
      if (status === 413) {
        setError(
          'That image is too large. Send a screenshot instead.'
        );
      } else if (!e?.response) {
        setError(
          "Your connection was interrupted. Your payment is safe — tap Send again."
        );
      } else {
        setError(
          e?.response?.data?.detail?.message ||
            e?.response?.data?.detail ||
            'That did not go through. Tap Send again.'
        );
      }
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="packages-page packages-page--centered">
        <div className="packages-spinner" aria-label="Loading" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="packages-page packages-page--centered">
        <p className="packages-muted">{error}</p>
        <button className="packages-ghost" onClick={() => navigate('/packages')}>
          Back to packages
        </button>
      </div>
    );
  }

  if (!data) return null;

  const price = `${data.amount} ${data.currency === 'ETB' ? 'birr' : data.currency}`;
  // Every "keep studying" on this page belongs INSIDE the package. `/dashboard`
  // is the ESSLCE dashboard: a student who had just sent a UAT receipt was
  // handed a different exam.
  const home = data.package.home || `/p/${cardKey}`;

  if (data.settled) {
    return (
      <div className="packages-page packages-page--centered">
        <h1 className="packages-title">{data.package.name} is open</h1>
        <p className="packages-muted packages-narrow">
          Paid and open. It&rsquo;s on your dashboard now.
        </p>
        <div className="packages-actions">
          <button className="package-card__cta" onClick={() => navigate(home)}>
            Start studying
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="packages-page packages-page--centered">
      <div className="checkout">
        <p className="packages-kicker">ክፍያ</p>
        <h1 className="packages-title">{data.package.name}</h1>

        <div className="checkout-line">
          <span>የአንድ ጊዜ ክፍያ · One-time payment</span>
          <span className="checkout-amount">{price}</span>
        </div>

        {data.note && <p className="packages-muted">{data.note}</p>}

        {data.mode === 'off' && (
          <>
            <p className="packages-muted packages-narrow">
              Payment is not open yet — <strong>please do not transfer money</strong>.
              Your place is saved. When it opens you&rsquo;ll pay {price} once and it
              opens immediately, with no receipt to send anyone.
            </p>
            <div className="packages-actions">
              <button className="packages-ghost" onClick={() => navigate('/packages')}>
                Back to packages
              </button>
              <button
                className="package-card__cta"
                onClick={() => navigate(home)}
              >
                Keep studying
              </button>
            </div>
          </>
        )}

        {data.mode === 'stub' && (
          <>
            <p className="checkout-testbanner">
              Test mode — no real money moves.
            </p>
            {error && <p className="chooser-error">{error}</p>}
            <div className="packages-actions">
              <button className="packages-ghost" onClick={() => navigate('/packages')}>
                Cancel
              </button>
              <button className="package-card__cta" onClick={pay} disabled={paying}>
                {paying ? 'One moment…' : `Pay ${price}`}
              </button>
            </div>
          </>
        )}

        {data.mode === 'manual' && (() => {
          const m = data.manual;
          const req = m?.request;
          const bank = m?.bank || {};
          const placeholder = Boolean(bank.is_placeholder);

          // Already with us, waiting on a person. Keyed off the server's flag
          // and not the status, because there is no 'submitted' status to read.
          if (req?.submitted && !resending) {
            return (
              <>
                <p className="checkout-state checkout-state--waiting">
                  Receipt received.
                </p>
                <p className="packages-muted packages-narrow">
                  We&rsquo;ll message you when your package is open — usually
                  within a few hours. You don&rsquo;t need to pay again or send
                  anything else.
                  {req.payment_reference && (
                    <>
                      {' '}Your reference: <strong>{req.payment_reference}</strong>
                    </>
                  )}
                </p>
                <div className="packages-actions">
                  <button className="package-card__cta" onClick={() => navigate(home)}>
                    Keep studying
                  </button>
                </div>
                {/* Sending the wrong screenshot is easy and, until now, final:
                    this screen had no way back to the form. The request is
                    still `pending`, so a second upload simply replaces the
                    first — no new request, no second transfer. */}
                <button
                  type="button"
                  className="checkout-linkbtn checkout-linkbtn--centered"
                  onClick={() => {
                    setResending(true);
                    setError(null);
                  }}
                >
                  Sent the wrong one? Send a different receipt
                </button>
              </>
            );
          }

          // Turned down. Say why, and let them try again on the same screen.
          const rejected = req?.status === 'rejected';

          return (
            <>
              {rejected && (
                <p className="checkout-state checkout-state--rejected">
                  {req?.rejection_reason ||
                    'We couldn&rsquo;t verify this receipt.'}{' '}
                  Send it again below and we&rsquo;ll look once more.
                </p>
              )}

              {/* Two sentences, and the second one answers the question the
                  old copy never did: WHERE the screenshot goes. A student was
                  told to "send us the receipt" on a page with no visible way
                  to send anything, so they went looking for a phone number. */}
              <p className="checkout-steps">
                <span className="checkout-steps__am">
                  {price} ይላኩ፣ ከዚያ የክፍያ ደረሰኝ ፎቶ እዚሁ ገጽ ላይ ይጫኑ።
                </span>
                <span>
                  Send {price} to the account below, then upload your payment
                  screenshot here on this page.
                </span>
              </p>

              {placeholder && (
                <p className="checkout-testbanner">
                  These are placeholder account details — do not transfer money yet.
                </p>
              )}

              {/* Two methods, two cards, each in its own brand colour so a
                  student recognises the one they use before reading a word.
                  The wordmarks are set in type rather than shipped as images:
                  we do not have licensed logo files, and inventing a bank's
                  logo on a page that asks for money is the last place to
                  improvise. Drop real assets into `checkout-pay__mark` when
                  they exist and nothing else has to move.

                  Numbers are tap-to-copy. Every one of these is retyped into
                  a banking app by hand, and a mistyped account number is a
                  transfer we can neither find nor refund. */}
              <div className="checkout-pay">
                <div className="checkout-pay__card checkout-pay__card--cbe">
                  <div className="checkout-pay__mark">CBE</div>
                  <div className="checkout-pay__body">
                    <span className="checkout-pay__name">
                      {bank.bank_name || 'Commercial Bank of Ethiopia'}
                    </span>
                    <span className="checkout-pay__holder">
                      {bank.account_name || '—'}
                    </span>
                    <button
                      type="button"
                      className="checkout-pay__num"
                      onClick={() => copyNumber(bank.account_number)}
                    >
                      <span>{bank.account_number || '—'}</span>
                      {/* An icon, not a word. "ቅዳ · Copy" was wider than the
                          label it sat beside and pushed a 13-digit account
                          number onto a second line on a phone — the one
                          string on this page that must never wrap. */}
                      <span className="checkout-pay__copy" aria-label="Copy">
                        {copied === bank.account_number ? '✓' : '⧉'}
                      </span>
                    </button>
                  </div>
                </div>

                {bank.telebirr_phone && (
                  <div className="checkout-pay__card checkout-pay__card--tb">
                    <div className="checkout-pay__mark">telebirr</div>
                    <div className="checkout-pay__body">
                      <span className="checkout-pay__name">Telebirr</span>
                      <span className="checkout-pay__holder">
                        {bank.account_name || '—'}
                      </span>
                      <button
                        type="button"
                        className="checkout-pay__num"
                        onClick={() => copyNumber(bank.telebirr_phone)}
                      >
                        <span>{bank.telebirr_phone}</span>
                        <span className="checkout-pay__copy" aria-label="Copy">
                          {copied === bank.telebirr_phone ? '✓' : '⧉'}
                        </span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* The steps were stored all along and never rendered, which is
                  why the page could not answer "where do I send it?". Amharic
                  list first, then English — two lists, not one line carrying
                  both languages. */}
              {(() => {
                const en: any = m?.instructions || {};
                const am: any = en.am || {};
                const steps: string[] = am.steps?.length ? am.steps : en.steps || [];
                const alt: string[] = am.steps?.length ? en.steps || [] : [];
                if (!steps.length) return null;
                return (
                  <div className="checkout-how">
                    <ol className="checkout-how__list checkout-how__list--am">
                      {steps.map((t) => (
                        <li key={t}>{t}</li>
                      ))}
                    </ol>
                    {alt.length > 0 && (
                      <ol className="checkout-how__list">
                        {alt.map((t) => (
                          <li key={t}>{t}</li>
                        ))}
                      </ol>
                    )}
                  </div>
                );
              })()}

              {(m?.instructions as any)?.am?.note && (
                <p className="checkout-how__note checkout-how__note--am">
                  {(m!.instructions as any).am.note}
                </p>
              )}
              {m?.instructions?.note && (
                <p className="checkout-how__note">{m.instructions.note}</p>
              )}

              {/* A request only accepts an upload while it is open. Rejected,
                  expired and cancelled ones are dead — posting to them fails —
                  so those states go back to the button, which mints a fresh
                  request. Only a live `pending` row gets the form. */}
              {!req || req.status !== 'pending' ? (
                <div className="packages-actions">
                  <button className="packages-ghost" onClick={() => navigate('/packages')}>
                    Back
                  </button>
                  <button className="package-card__cta" onClick={startBank} disabled={starting}>
                    {starting
                      ? 'One moment…'
                      : rejected
                      ? 'Send it again'
                      : 'Upload your payment screenshot'}
                  </button>
                </div>
              ) : (
                <>
                  {/* One input, reused for pick and replace. `accept` keeps the
                      phone's own picker to images and PDFs; no `capture`, so
                      the student still gets Camera *and* Gallery — the receipt
                      is nearly always already in their gallery. */}
                  <input
                    ref={fileInput}
                    id="checkout-receipt"
                    className="checkout-file"
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => pickProof(e.target.files?.[0])}
                  />

                  {!proof ? (
                    <label className="checkout-drop" htmlFor="checkout-receipt">
                      <span className="checkout-drop__icon" aria-hidden="true">🧾</span>
                      <span className="checkout-drop__title">
                        {preparing ? 'Checking receipt…' : 'ደረሰኙን ይላኩ · Upload your receipt'}
                      </span>
                      <span className="checkout-drop__hint">
                        Photo or screenshot.
                      </span>
                    </label>
                  ) : (
                    <div className="checkout-proof">
                      {proof.isPdf ? (
                        <div className="checkout-proof__pdf" aria-hidden="true">PDF</div>
                      ) : (
                        <img
                          className="checkout-proof__img"
                          src={proof.previewUrl}
                          alt="The receipt you are about to send"
                        />
                      )}
                      <div className="checkout-proof__meta">
                        <strong>Ready to send</strong>
                        <span className="checkout-proof__size">
                          {humanSize(proof.bytes)}
                        </span>
                        <div className="checkout-proof__acts">
                          <button
                            type="button"
                            className="checkout-linkbtn"
                            onClick={() => fileInput.current?.click()}
                            disabled={sending}
                          >
                            Change
                          </button>
                          <button
                            type="button"
                            className="checkout-linkbtn checkout-linkbtn--quiet"
                            onClick={clearProof}
                            disabled={sending}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {error && <p className="chooser-error">{error}</p>}

                  <div className="packages-actions">
                    <button
                      className="packages-ghost"
                      onClick={() => navigate(home)}
                      disabled={sending}
                    >
                      I&rsquo;ll send it later
                    </button>
                    <button
                      className="package-card__cta"
                      onClick={() => sendProof(req.request_id)}
                      disabled={sending || preparing || !proof}
                    >
                      {sending ? 'Sending…' : 'ደረሰኙን ይላኩ'}
                    </button>
                  </div>
                </>
              )}

              {error && !req && <p className="chooser-error">{error}</p>}
            </>
          );
        })()}

        {data.mode === 'live' && (
          <>
            <p className="packages-muted packages-narrow">
              Continue to Telebirr to pay {price}. It unlocks the moment the payment
              clears — you won&rsquo;t need to send a screenshot to anyone.
            </p>
            {error && <p className="chooser-error">{error}</p>}
            <div className="packages-actions">
              <button className="packages-ghost" onClick={() => navigate('/packages')}>
                Cancel
              </button>
              <button className="package-card__cta" disabled title="Coming next">
                Pay with Telebirr
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Checkout;
